"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { MediaShareEntry } from "./mediaSourceShare";
import { AuthContext } from "@/app/contexts";
import {
  buildEdgeLambdaUrl,
  fetchInferredCases,
  fetchSharedData,
} from "@/shared/cors-edge-lambda-api";
import { useCellServiceSession } from "@/hooks/useCellServiceSession";
import { withTokenAuthRecovery } from "@/shared/cors-auth-recovery";
import {
  describeCaptureSaveError,
  isCaptureSaveLogEnabled,
  logCaptureSave,
} from "@/shared/captureSaveLog";

/** DynamoDB / API case key for the all-cases view. */
export const ALL_CASES_STORAGE_ID = "*";

/** Max per-tenant case bundles to load when aggregating the all-cases drawer view. */
const ALL_CASES_AGGREGATE_FETCH_LIMIT = 40;

const MAX_SOURCES = 50;
const MAX_COPIES = 50;
export const MAX_CAPTURES = 15;
/** Max length for stored S3 URI strings in capture metadata. */
export const MAX_CAPTURE_S3_URI_CHARS = 2048;
/** @deprecated Legacy base64 limit — kept for reading old entries only. */
export const MAX_CAPTURE_BASE64_CHARS = 280_000;

/** Batch local edits before PUT; reduces API load and avoids overlapping full-item writes. */
const PERSIST_DEBOUNCE_MS = 650;
/** Cap CAS merge retries when many tabs race the same item. */
const MAX_REVISION_CONFLICT_RETRIES = 8;

import { stripWebpToRawBase64 } from "@/shared/userImageStorage";

export type ToolsSharedCaptureEntry = {
  id: string;
  s3Uri: string;
  /** Legacy — read-only compat for entries saved before S3 migration. */
  webpBase64?: string;
  createdAt: number;
  /** Content-moderation detection type (e.g. ``'CSAM'``) when the capture is of flagged content. */
  contentModerationType?: string;
  /** Raw base64 only (no `data:` prefix). Obfuscated variant shown by default for flagged captures. */
  obfuscatedWebpBase64?: string;
  /**
   * LLM-generated description of the flagged source image, snapshotted at
   * capture time from the source's file-info (a capture is a standalone data URL
   * with no later provenance link). Lets a reviewer read the description in the
   * favorite's detail view. Only populated for CSAM captures.
   */
  imageDescription?: string;
};

export type ToolsSharedCopyEntry = {
  id: string;
  text: string;
  timestamp: number;
};

export type ToolsSharedNote = {
  id: string;
  title: string;
  content: string;
  createdAt: number;
  updatedAt: number;
};

export type ToolsSharedBundle = {
  captures: ToolsSharedCaptureEntry[];
  sources: MediaShareEntry[];
  copies: ToolsSharedCopyEntry[];
  notes: ToolsSharedNote[];
};

const emptyBundle = (): ToolsSharedBundle => ({
  captures: [],
  sources: [],
  copies: [],
  notes: [],
});

export { stripWebpToRawBase64 } from "@/shared/userImageStorage";

export function normalizeCapturesList(
  entries: ToolsSharedCaptureEntry[],
): ToolsSharedCaptureEntry[] {
  return entries
    .map((e) => {
      const s3Uri = typeof e.s3Uri === "string" ? e.s3Uri.trim() : "";
      const legacyBase64 = e.webpBase64 ? stripWebpToRawBase64(e.webpBase64) : "";
      const obfRaw = e.obfuscatedWebpBase64
        ? stripWebpToRawBase64(e.obfuscatedWebpBase64)
        : undefined;
      const keepObf =
        !!obfRaw && obfRaw.length > 0 && obfRaw.length <= MAX_CAPTURE_BASE64_CHARS;
      return {
        id: e.id,
        s3Uri,
        ...(legacyBase64 ? { webpBase64: legacyBase64 } : {}),
        createdAt: e.createdAt,
        ...(e.contentModerationType ? { contentModerationType: e.contentModerationType } : {}),
        ...(keepObf ? { obfuscatedWebpBase64: obfRaw } : {}),
        ...(e.imageDescription ? { imageDescription: e.imageDescription } : {}),
      };
    })
    .filter(
      (e) =>
        (e.s3Uri.length > 0 && e.s3Uri.length <= MAX_CAPTURE_S3_URI_CHARS) ||
        (e.webpBase64 &&
          e.webpBase64.length > 0 &&
          e.webpBase64.length <= MAX_CAPTURE_BASE64_CHARS),
    )
    .slice(0, MAX_CAPTURES);
}

function capturesFromUnknown(arr: unknown[]): ToolsSharedCaptureEntry[] {
  const parsed: ToolsSharedCaptureEntry[] = [];
  for (const x of arr) {
    if (!x || typeof x !== "object") continue;
    const o = x as Record<string, unknown>;
    if (typeof o.id !== "string" || typeof o.createdAt !== "number") continue;

    const s3Uri = typeof o.s3Uri === "string" ? o.s3Uri : "";
    const webpBase64 = typeof o.webpBase64 === "string" ? o.webpBase64 : undefined;

    if (!s3Uri && !webpBase64) continue;

    parsed.push({
      id: o.id,
      s3Uri,
      ...(webpBase64 ? { webpBase64 } : {}),
      createdAt: o.createdAt,
      ...(typeof o.contentModerationType === "string"
        ? { contentModerationType: o.contentModerationType }
        : {}),
      ...(typeof o.obfuscatedWebpBase64 === "string"
        ? { obfuscatedWebpBase64: o.obfuscatedWebpBase64 }
        : {}),
      ...(typeof o.imageDescription === "string"
        ? { imageDescription: o.imageDescription }
        : {}),
    });
  }
  return normalizeCapturesList(parsed);
}

function normalizeBundle(raw: Partial<ToolsSharedBundle> | null | undefined): ToolsSharedBundle {
  const b = emptyBundle();
  if (!raw || typeof raw !== "object") return b;
  if (Array.isArray(raw.captures)) b.captures = capturesFromUnknown(raw.captures as unknown[]);
  if (Array.isArray(raw.sources)) b.sources = raw.sources.slice(0, MAX_SOURCES);
  if (Array.isArray(raw.copies)) b.copies = raw.copies.slice(0, MAX_COPIES);
  if (Array.isArray(raw.notes)) b.notes = raw.notes;
  return b;
}

function parseRevision(raw: unknown): number {
  if (!raw || typeof raw !== "object") return 0;
  const r = (raw as Record<string, unknown>).revision;
  if (typeof r === "number" && Number.isInteger(r) && r >= 0) {
    return r;
  }
  return 0;
}

/** Union by `id`; on duplicate keep the entry with the higher score (newer). */
function mergeByIdPreferNewer<T extends { id: string }>(
  local: T[],
  server: T[],
  score: (t: T) => number,
): T[] {
  const map = new Map<string, T>();
  for (const x of server) {
    map.set(x.id, x);
  }
  for (const x of local) {
    const existing = map.get(x.id);
    if (!existing || score(x) >= score(existing)) {
      map.set(x.id, x);
    }
  }
  return Array.from(map.values()).sort((a, b) => score(b) - score(a));
}

function isAllCasesStorageCaseId(id: string): boolean {
  const t = id.trim();
  return !t || t === ALL_CASES_STORAGE_ID || t.toLowerCase() === "all cases";
}

/**
 * Case id stored in DynamoDB (no tenant prefix; tenant is a separate attribute).
 * ``*`` and "All Cases" both map to {@link ALL_CASES_STORAGE_ID}.
 */
export function normalizeSharedDataCaseId(
  caseId: string | undefined | null,
  tenantFriendlyName?: string | null,
): string {
  const raw = (caseId ?? "").trim();
  if (isAllCasesStorageCaseId(raw)) {
    return ALL_CASES_STORAGE_ID;
  }
  let tf = (tenantFriendlyName ?? "").trim();
  if (tf) {
    tf = tf.replace(/^\/+/g, "").replace(/\/+$/g, "").trim();
    if (tf) {
      const prefix = `${tf}/`;
      let id = raw.replace(/^\/+/, "");
      if (id.startsWith(prefix)) {
        id = id.slice(prefix.length);
      }
      return isAllCasesStorageCaseId(id) ? ALL_CASES_STORAGE_ID : id;
    }
  }
  const parts = raw.split("/");
  const result = parts.length > 1 ? parts.slice(1).join("/") : raw;
  return isAllCasesStorageCaseId(result) ? ALL_CASES_STORAGE_ID : result;
}

export function isAllCasesSharedView(
  caseId: string | undefined | null,
  tenantFriendlyName?: string | null,
): boolean {
  return normalizeSharedDataCaseId(caseId, tenantFriendlyName) === ALL_CASES_STORAGE_ID;
}

/** True when two case ids resolve to the same tools-drawer storage key. */
export function sharedDataCasesMatch(
  a: string | undefined | null,
  b: string | undefined | null,
  tenantFriendlyName?: string | null,
): boolean {
  if (!a?.trim() || !b?.trim()) return false;
  return (
    normalizeSharedDataCaseId(a, tenantFriendlyName) ===
    normalizeSharedDataCaseId(b, tenantFriendlyName)
  );
}

/** LocalStorage key scoped to tenant + case (matches remote Dynamo partition). */
export function buildSharedDataLocalStorageKey(
  prefix: string,
  caseId: string | undefined,
  tenantFriendlyName: string | null | undefined,
): string {
  const tenant = (tenantFriendlyName ?? "").trim() || "no-tenant";
  const caseKey = normalizeSharedDataCaseId(caseId, tenantFriendlyName) || "no-case";
  return `${prefix}-${tenant}-${caseKey}`;
}

function bundleHasEntries(bundle: ToolsSharedBundle): boolean {
  return (
    bundle.captures.length > 0 ||
    bundle.copies.length > 0 ||
    bundle.notes.length > 0 ||
    bundle.sources.length > 0
  );
}

type CaseBundleRow = { bundle: ToolsSharedBundle; revision: number };

function sortStorageCaseIdsForAllCasesView(caseIds: string[]): string[] {
  return [...caseIds].sort((a, b) => {
    if (a === ALL_CASES_STORAGE_ID && b !== ALL_CASES_STORAGE_ID) return -1;
    if (b === ALL_CASES_STORAGE_ID && a !== ALL_CASES_STORAGE_ID) return 1;
    return a.localeCompare(b);
  });
}

function mergeBundlesForAllCasesDisplay(
  bundlesByCase: Map<string, CaseBundleRow>,
): { merged: ToolsSharedBundle; entryOwners: Map<string, string> } {
  const entryOwners = new Map<string, string>();
  const merged = emptyBundle();
  for (const storageCaseId of sortStorageCaseIdsForAllCasesView([...bundlesByCase.keys()])) {
    const row = bundlesByCase.get(storageCaseId);
    if (!row) continue;
    for (const c of row.bundle.captures) {
      entryOwners.set(c.id, storageCaseId);
      merged.captures.push(c);
    }
    for (const c of row.bundle.copies) {
      entryOwners.set(c.id, storageCaseId);
      merged.copies.push(c);
    }
    for (const n of row.bundle.notes) {
      entryOwners.set(n.id, storageCaseId);
      merged.notes.push(n);
    }
    for (const s of row.bundle.sources) {
      entryOwners.set(s.id, storageCaseId);
      merged.sources.push(s);
    }
  }
  return { merged: normalizeBundle(merged), entryOwners };
}

function parseBundlesListResponse(
  raw: unknown,
  tenantFriendlyName: string | null | undefined,
): Map<string, CaseBundleRow> | null {
  if (!raw || typeof raw !== "object") return null;
  const bundles = (raw as Record<string, unknown>).bundles;
  const map = new Map<string, CaseBundleRow>();
  if (Array.isArray(bundles)) {
    for (const row of bundles) {
      if (!row || typeof row !== "object") continue;
      const o = row as Record<string, unknown>;
      const caseKey =
        typeof o.caseId === "string"
          ? o.caseId
          : typeof o.case_id === "string"
            ? o.case_id
            : null;
      if (!caseKey) continue;
      const storageCaseId = normalizeSharedDataCaseId(caseKey, tenantFriendlyName);
      map.set(storageCaseId, {
        bundle: normalizeBundle(o as Partial<ToolsSharedBundle>),
        revision: parseRevision(o),
      });
    }
    return map.size > 0 ? map : null;
  }
  if (bundles && typeof bundles === "object" && !Array.isArray(bundles)) {
    for (const [caseKey, row] of Object.entries(bundles as Record<string, unknown>)) {
      if (!row || typeof row !== "object") continue;
      const storageCaseId = normalizeSharedDataCaseId(caseKey, tenantFriendlyName);
      map.set(storageCaseId, {
        bundle: normalizeBundle(row as Partial<ToolsSharedBundle>),
        revision: parseRevision(row),
      });
    }
    return map.size > 0 ? map : null;
  }
  return null;
}

function mergeBundles(local: ToolsSharedBundle, server: ToolsSharedBundle): ToolsSharedBundle {
  const captures = mergeByIdPreferNewer(
    local.captures,
    server.captures,
    (c) => c.createdAt,
  );
  const copies = mergeByIdPreferNewer(local.copies, server.copies, (c) => c.timestamp);
  const notes = mergeByIdPreferNewer(local.notes, server.notes, (n) => n.updatedAt);
  const sources = mergeByIdPreferNewer(local.sources, server.sources, (s) => s.timestamp);
  return normalizeBundle({ captures, copies, notes, sources });
}

export async function getSharedData(
  caseId: string,
  dataType: string = "all",
  session: { cellId: string; accessToken: string },
  action: "get" | "list" = "get",
  /** Optional: strip tenant-prefixed display case ids before the API call. */
  tenantFriendlyName?: string | null,
) {
  if (!session.accessToken?.trim()) {
    throw new Error("getSharedData: access token is required");
  }
  const storageCaseId = normalizeSharedDataCaseId(caseId, tenantFriendlyName);
  return fetchSharedData<Partial<ToolsSharedBundle>>({
    cellId: session.cellId,
    accessToken: session.accessToken,
    body: {
      caseId: storageCaseId,
      action,
      dataType,
    },
  });
}

async function fetchAllCaseBundles(
  session: { cellId: string; accessToken: string },
  tenantFriendlyName?: string | null,
): Promise<Map<string, CaseBundleRow>> {
  /** /cases/inferred/list still scopes by body.tenant (unlike tools-drawer). */
  const tenant = (tenantFriendlyName ?? "").trim() || null;
  const map = new Map<string, CaseBundleRow>();

  try {
    const listRaw = await getSharedData(
      ALL_CASES_STORAGE_ID,
      "all",
      session,
      "list",
      tenantFriendlyName,
    );
    const listed = parseBundlesListResponse(listRaw, tenantFriendlyName);
    if (listed) {
      for (const [id, row] of listed) {
        map.set(id, row);
      }
      if (!map.has(ALL_CASES_STORAGE_ID)) {
        map.set(ALL_CASES_STORAGE_ID, { bundle: emptyBundle(), revision: 0 });
      }
      return map;
    }
  } catch {
    /* list action optional until edge-lambda supports it */
  }

  const primaryRaw = await getSharedData(
    ALL_CASES_STORAGE_ID,
    "all",
    session,
    "get",
    tenantFriendlyName,
  );
  map.set(ALL_CASES_STORAGE_ID, {
    bundle: normalizeBundle(primaryRaw),
    revision: parseRevision(primaryRaw),
  });

  if (tenant) {
    try {
      const inferred = await fetchInferredCases({
        cellId: session.cellId,
        accessToken: session.accessToken,
        primary_only: true,
        tenant,
      });
      const otherIds = [
        ...new Set(
          (inferred.options ?? [])
            .map((opt) => normalizeSharedDataCaseId(opt, tenantFriendlyName))
            .filter((id) => id !== ALL_CASES_STORAGE_ID),
        ),
      ].slice(0, ALL_CASES_AGGREGATE_FETCH_LIMIT);

      await Promise.all(
        otherIds.map(async (storageCaseId) => {
          try {
            const raw = await getSharedData(
              storageCaseId,
              "all",
              session,
              "get",
              tenantFriendlyName,
            );
            const bundle = normalizeBundle(raw);
            if (bundleHasEntries(bundle)) {
              map.set(storageCaseId, { bundle, revision: parseRevision(raw) });
            }
          } catch {
            /* skip cases with no row or fetch errors */
          }
        }),
      );
    } catch {
      /* inferred list is best-effort for aggregate view */
    }
  }

  return map;
}

export type UpdateSharedDataResult =
  | { ok: true; revision: number }
  | {
      ok: false;
      conflict: true;
      serverRevision: number;
      serverBundle: ToolsSharedBundle;
    };

export async function updateSharedData(
  caseId: string,
  bundle: ToolsSharedBundle,
  expectedRevision: number,
  session: { cellId: string; accessToken: string },
  /** Optional: strip tenant-prefixed display case ids before the API call. */
  tenantFriendlyName?: string | null,
): Promise<UpdateSharedDataResult> {
  if (!session.accessToken?.trim()) {
    throw new Error("updateSharedData: access token is required");
  }
  const storageCaseId = normalizeSharedDataCaseId(caseId, tenantFriendlyName);

  const url = buildEdgeLambdaUrl(session.cellId, "/tools-drawer");

  // The read side of this bundle goes through ``edgeRequest``, which recovers
  // from a stale access token; this write was the one call that did not, so a
  // token the proactive refresh loop failed to renew turned every queued edit
  // (a saved capture, most visibly) into a console-only failure. Rebuild the
  // Authorization header from the token the wrapper supplies so the retry uses
  // the refreshed credentials.
  return withTokenAuthRecovery(session.accessToken, async (accessToken) => {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        caseId: storageCaseId,
        action: "update",
        revision: expectedRevision,
        captures: bundle.captures,
        copies: bundle.copies,
        notes: bundle.notes,
        sources: bundle.sources,
      }),
    });

    if (res.status === 409) {
      let body: Record<string, unknown> = {};
      try {
        body = (await res.json()) as Record<string, unknown>;
      } catch {
        /* empty body */
      }
      const serverRevision = parseRevision(body);
      return {
        ok: false,
        conflict: true,
        serverRevision,
        serverBundle: normalizeBundle(body as Partial<ToolsSharedBundle>),
      };
    }
    if (!res.ok) {
      const t = await res.text();
      // Carry the status so an authorizer rejection that *did* answer with CORS
      // headers is recognised as one and retried, rather than only the opaque
      // case that surfaces as a thrown TypeError.
      throw Object.assign(new Error(t || res.statusText), { status: res.status });
    }
    let data: Record<string, unknown> = {};
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      /* ignore */
    }
    const rev = data.revision;
    const revision =
      typeof rev === "number" && Number.isInteger(rev) && rev >= 0
        ? rev
        : expectedRevision + 1;
    return { ok: true, revision };
  });
}

export type ToolsSharedCaseContextValue = {
  caseId: string;
  tenantFriendlyName: string | null;
  hydrated: boolean;
  captures: ToolsSharedCaptureEntry[];
  setCaptures: React.Dispatch<React.SetStateAction<ToolsSharedCaptureEntry[]>>;
  sources: MediaShareEntry[];
  setSources: React.Dispatch<React.SetStateAction<MediaShareEntry[]>>;
  copies: ToolsSharedCopyEntry[];
  setCopies: React.Dispatch<React.SetStateAction<ToolsSharedCopyEntry[]>>;
  notes: ToolsSharedNote[];
  setNotes: React.Dispatch<React.SetStateAction<ToolsSharedNote[]>>;
  /** Re-fetch the case bundle from the server and replace in-memory state (skips a redundant persist round-trip). */
  refetch: () => Promise<void>;
};

const ToolsSharedCaseDataContext = createContext<ToolsSharedCaseContextValue | null>(
  null,
);

export function useOptionalToolsSharedCaseData() {
  return useContext(ToolsSharedCaseDataContext);
}

/**
 * Shared state + effects for a case-bound bundle. When `caseId` is falsy, returns `null` and
 * resets internal state so the parent can always render a single `Context.Provider` root — avoiding
 * swapping `Provider` vs another component type, which would remount all descendants.
 */
function useToolsSharedCaseContextValue(
  caseId: string | undefined,
  tenantFriendlyName: string | null | undefined,
  cellIdFromInit?: string | null,
): ToolsSharedCaseContextValue | null {
  const auth = useContext(AuthContext);
  const { cellId: cellIdFromToken, accessToken: cellAccessToken } =
    useCellServiceSession();
  const resolvedCellId =
    cellIdFromToken ?? (cellIdFromInit?.trim() || null) ?? null;
  const sessionReady = Boolean(resolvedCellId && cellAccessToken);
  const sessionRef = useRef<{ cellId: string; accessToken: string } | null>(null);
  sessionRef.current =
    resolvedCellId && cellAccessToken
      ? { cellId: resolvedCellId, accessToken: cellAccessToken }
      : null;

  const tenantRef = useRef<string | null>(null);
  tenantRef.current =
    (tenantFriendlyName ?? auth.tenantName ?? "").trim() || null;

  const storageCaseId = caseId
    ? normalizeSharedDataCaseId(caseId, tenantRef.current)
    : undefined;
  const allCasesView = storageCaseId === ALL_CASES_STORAGE_ID;

  const [hydrated, setHydrated] = useState(false);
  const [bundle, setBundle] = useState<ToolsSharedBundle>(emptyBundle);
  const skipNextPersist = useRef(true);
  const bundleRef = useRef(bundle);
  const bundleVersionRef = useRef(0);
  /** Last known DynamoDB `revision` for the active single-case write target. */
  const bundleRevisionRef = useRef(0);
  /** Per storage-case revision when aggregating the all-cases view. */
  const multiRevisionsRef = useRef<Map<string, number>>(new Map());
  /** Per storage-case bundles when aggregating the all-cases view. */
  const multiBundlesRef = useRef<Map<string, ToolsSharedBundle>>(new Map());
  /** Maps shared entry id → storage case id (all-cases aggregate view). */
  const entryOwnerRef = useRef<Map<string, string>>(new Map());
  const dirtyStorageCasesRef = useRef<Set<string>>(new Set());
  const aggregateViewRef = useRef(allCasesView);
  aggregateViewRef.current = allCasesView;
  /** Per-case monotonic seq so a flush for case A is not invalidated when case B hydrates and resets global state. */
  const persistSeqByCaseRef = useRef<Map<string, number>>(new Map());
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The most recent flush, while it is still running. `refetch` awaits it so a GET never races a PUT. */
  const persistInFlightRef = useRef<Promise<void> | null>(null);
  /** Active view `caseId` for this render; used so a late async flush for a previous case does not stomp refs/state. */
  const currentCaseIdRef = useRef<string | undefined>(caseId);
  currentCaseIdRef.current = caseId;

  bundleRef.current = bundle;

  const applySingleBundle = useCallback((raw: unknown) => {
    skipNextPersist.current = true;
    bundleRevisionRef.current = parseRevision(raw);
    multiBundlesRef.current.clear();
    multiRevisionsRef.current.clear();
    entryOwnerRef.current.clear();
    dirtyStorageCasesRef.current.clear();
    setBundle(normalizeBundle(raw as Partial<ToolsSharedBundle>));
  }, []);

  const applyAggregateBundles = useCallback((bundlesByCase: Map<string, CaseBundleRow>) => {
    skipNextPersist.current = true;
    multiBundlesRef.current.clear();
    multiRevisionsRef.current.clear();
    entryOwnerRef.current.clear();
    dirtyStorageCasesRef.current.clear();
    for (const [id, row] of bundlesByCase) {
      multiBundlesRef.current.set(id, row.bundle);
      multiRevisionsRef.current.set(id, row.revision);
    }
    const { merged, entryOwners } = mergeBundlesForAllCasesDisplay(bundlesByCase);
    entryOwnerRef.current = entryOwners;
    bundleRevisionRef.current = multiRevisionsRef.current.get(ALL_CASES_STORAGE_ID) ?? 0;
    setBundle(merged);
  }, []);

  const markHydratedFromFetch = useCallback(() => {
    setHydrated(true);
    skipNextPersist.current = true;
  }, []);

  const fetchAndApplyBundle = useCallback(
    async (isStale?: () => boolean) => {
      if (!caseId) return false;
      const session = sessionRef.current;
      if (!session) return false;
      if (aggregateViewRef.current) {
        const bundlesByCase = await fetchAllCaseBundles(
          session,
          tenantRef.current,
        );
        if (isStale?.()) return false;
        applyAggregateBundles(bundlesByCase);
        return true;
      }
      const raw = await getSharedData(
        caseId,
        "all",
        session,
        "get",
        tenantRef.current,
      );
      if (isStale?.()) return false;
      applySingleBundle(raw);
      return true;
    },
    [caseId, applySingleBundle, applyAggregateBundles],
  );

  useEffect(() => {
    if (!caseId || !sessionReady) {
      skipNextPersist.current = true;
      bundleVersionRef.current = 0;
      bundleRevisionRef.current = 0;
      multiBundlesRef.current.clear();
      multiRevisionsRef.current.clear();
      entryOwnerRef.current.clear();
      dirtyStorageCasesRef.current.clear();
      setHydrated(false);
      setBundle(emptyBundle());
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
      return;
    }

    let cancelled = false;
    skipNextPersist.current = true;
    if (storageCaseId) {
      persistSeqByCaseRef.current.delete(storageCaseId);
    }
    bundleVersionRef.current = 0;
    bundleRevisionRef.current = 0;
    multiBundlesRef.current.clear();
    multiRevisionsRef.current.clear();
    entryOwnerRef.current.clear();
    dirtyStorageCasesRef.current.clear();
    setHydrated(false);
    setBundle(emptyBundle());
    (async () => {
      try {
        const session = sessionRef.current;
        if (!session) return;
        const applied = await fetchAndApplyBundle(() => cancelled);
        if (cancelled) return;
        if (!applied && !sessionRef.current) return;
        markHydratedFromFetch();
      } catch (e) {
        console.error("getSharedData failed", { caseId, error: e });
        if (!cancelled) {
          setBundle(emptyBundle());
          markHydratedFromFetch();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    caseId,
    sessionReady,
    storageCaseId,
    resolvedCellId,
    cellAccessToken,
    applySingleBundle,
    applyAggregateBundles,
    fetchAndApplyBundle,
    markHydratedFromFetch,
  ]);

  const recomputeAggregateDisplay = useCallback(() => {
    const bundlesByCase = new Map<string, CaseBundleRow>();
    for (const [id, b] of multiBundlesRef.current) {
      bundlesByCase.set(id, {
        bundle: b,
        revision: multiRevisionsRef.current.get(id) ?? 0,
      });
    }
    const { merged, entryOwners } = mergeBundlesForAllCasesDisplay(bundlesByCase);
    entryOwnerRef.current = entryOwners;
    bundleRef.current = merged;
    bundleRevisionRef.current = multiRevisionsRef.current.get(ALL_CASES_STORAGE_ID) ?? 0;
    setBundle(merged);
  }, []);

  const flushOneStorageCase = useCallback(
    async (
      storageId: string,
      frozen?: { snapshot: ToolsSharedBundle; revision: number },
      /** Optional tenant for display-case id normalization on unmount flush. */
      tenantForNormalize?: string | null,
    ) => {
      const tenant = (tenantForNormalize ?? tenantRef.current)?.trim() || null;
      const session = sessionRef.current;
      if (!session) return;

      const useFrozen = !!frozen;
      let snapshot =
        frozen?.snapshot ??
        (aggregateViewRef.current
          ? multiBundlesRef.current.get(storageId) ?? emptyBundle()
          : bundleRef.current);
      let expectedRevision =
        frozen?.revision ??
        (aggregateViewRef.current
          ? multiRevisionsRef.current.get(storageId) ?? 0
          : bundleRevisionRef.current);

      let conflictRetries = 0;
      for (;;) {
        const seqMap = persistSeqByCaseRef.current;
        const prevSeq = seqMap.get(storageId) ?? 0;
        const mySeq = prevSeq + 1;
        seqMap.set(storageId, mySeq);
        const versionAtStart = bundleVersionRef.current;
        let result: UpdateSharedDataResult;
        try {
          result = await updateSharedData(
            storageId,
            snapshot,
            expectedRevision,
            session,
            tenant,
          );
        } catch (e) {
          if (seqMap.get(storageId) !== mySeq) return;
          // Terminal for anything in this snapshot: nothing retries after this,
          // so a capture that uploaded fine is dropped here and comes back
          // missing on the next load.
          if (isCaptureSaveLogEnabled()) {
            logCaptureSave("error", "favorites-persist failed", {
              storageCaseId: storageId,
              expectedRevision,
              captures: snapshot.captures?.length ?? 0,
              unmountFlush: useFrozen,
              ...describeCaptureSaveError(e),
            });
          } else {
            console.error("updateSharedData failed", e);
          }
          return;
        }
        if (seqMap.get(storageId) !== mySeq) return;
        if (!result.ok) {
          if (conflictRetries++ >= MAX_REVISION_CONFLICT_RETRIES) {
            if (isCaptureSaveLogEnabled()) {
              logCaptureSave(
                "error",
                "favorites-persist failed: too many revision conflicts, giving up",
                {
                  storageCaseId: storageId,
                  expectedRevision,
                  captures: snapshot.captures?.length ?? 0,
                  conflictRetries,
                },
              );
            } else {
              console.error("updateSharedData: too many revision conflicts, giving up");
            }
            return;
          }
          snapshot = mergeBundles(snapshot, result.serverBundle);
          expectedRevision = result.serverRevision;
          if (aggregateViewRef.current) {
            multiBundlesRef.current.set(storageId, snapshot);
            multiRevisionsRef.current.set(storageId, result.serverRevision);
            if (
              currentCaseIdRef.current &&
              isAllCasesSharedView(currentCaseIdRef.current, tenant)
            ) {
              recomputeAggregateDisplay();
            }
          } else if (
            storageId ===
            normalizeSharedDataCaseId(currentCaseIdRef.current, tenant)
          ) {
            bundleRevisionRef.current = result.serverRevision;
            bundleRef.current = snapshot;
            setBundle(snapshot);
          }
          continue;
        }
        conflictRetries = 0;
        if (aggregateViewRef.current) {
          multiBundlesRef.current.set(storageId, snapshot);
          multiRevisionsRef.current.set(storageId, result.revision);
          if (
            currentCaseIdRef.current &&
            isAllCasesSharedView(currentCaseIdRef.current, tenant)
          ) {
            bundleRevisionRef.current =
              storageId === ALL_CASES_STORAGE_ID
                ? result.revision
                : bundleRevisionRef.current;
            if (!useFrozen) {
              recomputeAggregateDisplay();
            }
          }
        } else if (
          storageId === normalizeSharedDataCaseId(currentCaseIdRef.current, tenant)
        ) {
          bundleRevisionRef.current = result.revision;
        }
        if (useFrozen) break;
        if (
          !aggregateViewRef.current &&
          storageId !== normalizeSharedDataCaseId(currentCaseIdRef.current, tenant)
        ) {
          break;
        }
        if (bundleVersionRef.current === versionAtStart) break;
        snapshot = aggregateViewRef.current
          ? multiBundlesRef.current.get(storageId) ?? emptyBundle()
          : bundleRef.current;
        expectedRevision = aggregateViewRef.current
          ? multiRevisionsRef.current.get(storageId) ?? 0
          : bundleRevisionRef.current;
      }
      dirtyStorageCasesRef.current.delete(storageId);
    },
    [recomputeAggregateDisplay],
  );

  const flushPersist = useCallback(
    async (
      caseIdForWrite?: string,
      frozen?: { snapshot: ToolsSharedBundle; revision: number },
    ) => {
      if (aggregateViewRef.current) {
        const targets =
          caseIdForWrite != null
            ? [normalizeSharedDataCaseId(caseIdForWrite, tenantRef.current)]
            : dirtyStorageCasesRef.current.size > 0
              ? [...dirtyStorageCasesRef.current]
              : [ALL_CASES_STORAGE_ID];
        // `frozen` is a single-case snapshot; only apply when flushing one storage id.
        const useFrozen = frozen != null && targets.length === 1;
        for (const storageId of targets) {
          const snap = useFrozen
            ? frozen.snapshot
            : multiBundlesRef.current.get(storageId);
          if (!snap) continue;
          const rev = useFrozen
            ? frozen.revision
            : multiRevisionsRef.current.get(storageId) ?? 0;
          await flushOneStorageCase(storageId, { snapshot: snap, revision: rev });
        }
        return;
      }
      const storageId =
        normalizeSharedDataCaseId(caseIdForWrite ?? caseId, tenantRef.current) ||
        undefined;
      if (!storageId) return;
      await flushOneStorageCase(storageId, frozen);
    },
    [caseId, flushOneStorageCase],
  );

  /** Run a flush and publish it on `persistInFlightRef` so `refetch` can await it. */
  const runFlush = useCallback(
    (caseIdForWrite?: string) => {
      const p = flushPersist(caseIdForWrite).catch((e) => {
        console.error("flushPersist failed", e);
      });
      persistInFlightRef.current = p;
      void p.then(() => {
        if (persistInFlightRef.current === p) persistInFlightRef.current = null;
      });
      return p;
    },
    [flushPersist],
  );

  useEffect(() => {
    bundleVersionRef.current += 1;
  }, [bundle]);

  useEffect(() => {
    if (!caseId || !hydrated) return;
    if (skipNextPersist.current) {
      skipNextPersist.current = false;
      return;
    }
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      persistTimerRef.current = null;
      void runFlush();
    }, PERSIST_DEBOUNCE_MS);
    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
    };
  }, [bundle, hydrated, caseId, runFlush]);

  /** Settle a debounced or in-flight write before reading from the server.
   *  `applySingleBundle` replaces state wholesale and clears the dirty set, so a GET
   *  issued while an edit is still in the debounce window would drop that edit from
   *  memory *and* cancel its pending write. */
  const flushPendingWrites = useCallback(async () => {
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
      await runFlush();
      return;
    }
    if (persistInFlightRef.current) await persistInFlightRef.current;
  }, [runFlush]);

  const refetch = useCallback(async () => {
    if (!caseId) return;
    const targetCaseId = caseId;
    try {
      await flushPendingWrites();
      if (currentCaseIdRef.current !== targetCaseId) return;
      const applied = await fetchAndApplyBundle(
        () => currentCaseIdRef.current !== targetCaseId,
      );
      if (applied) markHydratedFromFetch();
    } catch (e) {
      console.error("getSharedData failed", e);
    }
  }, [caseId, fetchAndApplyBundle, markHydratedFromFetch, flushPendingWrites]);

  // Flush pending edits on case switch or unmount only — not on every `bundle` change (the persist
  // effect above re-runs on each edit; its cleanup must not call flush or debounce is defeated).
  // `useLayoutEffect`: cleanup runs before passive `useEffect` (hydration). If we used `useEffect`
  // here, `void flushPersist()` would defer reading `bundleRef` to a microtask; hydration could
  // `setBundle(emptyBundle())` and a re-render would point `bundleRef` at the new case's empty
  // bundle before the flush ran — wiping the previous case in DynamoDB.
  useLayoutEffect(() => {
    const id = caseId;
    // Capture tenant for this effect generation — tenantRef.current is updated each render
    // before cleanup runs, so reading the ref in cleanup would use the *next* tenant.
    const tenant = (tenantFriendlyName ?? "").trim() || null;
    const wasAggregate = aggregateViewRef.current;
    return () => {
      if (!id || !sessionRef.current) return;
      if (wasAggregate) {
        const dirty = [...dirtyStorageCasesRef.current];
        if (dirty.length === 0) {
          dirty.push(ALL_CASES_STORAGE_ID);
        }
        for (const storageId of dirty) {
          const snapshot = multiBundlesRef.current.get(storageId);
          if (!snapshot) continue;
          const revision = multiRevisionsRef.current.get(storageId) ?? 0;
          void flushOneStorageCase(storageId, { snapshot, revision }, tenant);
        }
        return;
      }
      const storageId = normalizeSharedDataCaseId(id, tenant);
      const snapshot = bundleRef.current;
      const revision = bundleRevisionRef.current;
      void flushOneStorageCase(storageId, { snapshot, revision }, tenant);
    };
  }, [caseId, flushOneStorageCase, tenantFriendlyName]);

  const applyListMutation = useCallback(
    <T extends { id: string }>(
      field: keyof ToolsSharedBundle,
      resolveNext: (prevList: T[]) => T[],
      normalizeNext: (list: T[]) => T[],
      defaultOwner: string,
    ) => {
      if (!aggregateViewRef.current) {
        setBundle((prev) => {
          const prevList = prev[field] as unknown as T[];
          return {
            ...prev,
            [field]: normalizeNext(resolveNext(prevList)),
          };
        });
        if (storageCaseId) dirtyStorageCasesRef.current.add(storageCaseId);
        return;
      }

      const prevList = bundleRef.current[field] as unknown as T[];
      const nextList = normalizeNext(resolveNext(prevList));
      const prevById = new Map(prevList.map((x) => [x.id, x]));
      const nextById = new Map(nextList.map((x) => [x.id, x]));

      for (const [id] of prevById) {
        if (!nextById.has(id)) {
          const owner = entryOwnerRef.current.get(id) ?? defaultOwner;
          const ownerBundle = multiBundlesRef.current.get(owner) ?? emptyBundle();
          const list = ownerBundle[field] as unknown as T[];
          multiBundlesRef.current.set(owner, {
            ...ownerBundle,
            [field]: list.filter((x) => x.id !== id),
          });
          entryOwnerRef.current.delete(id);
          dirtyStorageCasesRef.current.add(owner);
        }
      }
      for (const [id, item] of nextById) {
        if (!prevById.has(id)) {
          const owner = defaultOwner;
          entryOwnerRef.current.set(id, owner);
          const ownerBundle = multiBundlesRef.current.get(owner) ?? emptyBundle();
          const list = ownerBundle[field] as unknown as T[];
          multiBundlesRef.current.set(owner, {
            ...ownerBundle,
            [field]: normalizeNext([...list.filter((x) => x.id !== id), item]),
          });
          dirtyStorageCasesRef.current.add(owner);
        } else if (prevById.get(id) !== item) {
          const owner = entryOwnerRef.current.get(id) ?? defaultOwner;
          const ownerBundle = multiBundlesRef.current.get(owner) ?? emptyBundle();
          const list = ownerBundle[field] as unknown as T[];
          multiBundlesRef.current.set(owner, {
            ...ownerBundle,
            [field]: list.map((x) => (x.id === id ? item : x)),
          });
          dirtyStorageCasesRef.current.add(owner);
        }
      }
      recomputeAggregateDisplay();
    },
    [recomputeAggregateDisplay, storageCaseId],
  );

  const writeOwnerForNewEntries = storageCaseId ?? ALL_CASES_STORAGE_ID;

  const setCaptures = useCallback(
    (action: React.SetStateAction<ToolsSharedCaptureEntry[]>) => {
      applyListMutation<ToolsSharedCaptureEntry>(
        "captures",
        (prev) =>
          typeof action === "function"
            ? (action as (c: ToolsSharedCaptureEntry[]) => ToolsSharedCaptureEntry[])(
                prev,
              )
            : action,
        normalizeCapturesList,
        writeOwnerForNewEntries,
      );
    },
    [applyListMutation, writeOwnerForNewEntries],
  );

  const setSources = useCallback(
    (action: React.SetStateAction<MediaShareEntry[]>) => {
      applyListMutation<MediaShareEntry>(
        "sources",
        (prev) =>
          typeof action === "function"
            ? (action as (s: MediaShareEntry[]) => MediaShareEntry[])(prev)
            : action,
        (list) => list.slice(0, MAX_SOURCES),
        writeOwnerForNewEntries,
      );
    },
    [applyListMutation, writeOwnerForNewEntries],
  );

  const setCopies = useCallback(
    (action: React.SetStateAction<ToolsSharedCopyEntry[]>) => {
      applyListMutation<ToolsSharedCopyEntry>(
        "copies",
        (prev) =>
          typeof action === "function"
            ? (action as (c: ToolsSharedCopyEntry[]) => ToolsSharedCopyEntry[])(
                prev,
              )
            : action,
        (list) => list.slice(0, MAX_COPIES),
        writeOwnerForNewEntries,
      );
    },
    [applyListMutation, writeOwnerForNewEntries],
  );

  const setNotes = useCallback(
    (action: React.SetStateAction<ToolsSharedNote[]>) => {
      applyListMutation<ToolsSharedNote>(
        "notes",
        (prev) =>
          typeof action === "function"
            ? (action as (n: ToolsSharedNote[]) => ToolsSharedNote[])(prev)
            : action,
        (list) => list,
        writeOwnerForNewEntries,
      );
    },
    [applyListMutation, writeOwnerForNewEntries],
  );

  return useMemo((): ToolsSharedCaseContextValue | null => {
    if (!caseId || !sessionReady) return null;
    return {
      caseId,
      tenantFriendlyName: tenantRef.current,
      hydrated,
      captures: bundle.captures,
      setCaptures,
      sources: bundle.sources,
      setSources,
      copies: bundle.copies,
      setCopies,
      notes: bundle.notes,
      setNotes,
      refetch,
    };
  }, [
    caseId,
    sessionReady,
    tenantFriendlyName,
    hydrated,
    bundle.captures,
    bundle.sources,
    bundle.copies,
    bundle.notes,
    setCaptures,
    setSources,
    setCopies,
    setNotes,
    refetch,
  ]);
}

export function ToolsSharedCaseDataProvider({
  caseId,
  tenantFriendlyName,
  cellIdFromInit,
  children,
}: {
  caseId?: string;
  tenantFriendlyName?: string | null;
  /** Fallback ``custom:cell_id`` from ``/api/init`` when the ID token omits it. */
  cellIdFromInit?: string | null;
  children: React.ReactNode;
}) {
  const value = useToolsSharedCaseContextValue(
    caseId,
    tenantFriendlyName,
    cellIdFromInit,
  );
  return (
    <ToolsSharedCaseDataContext.Provider value={value}>
      {children}
    </ToolsSharedCaseDataContext.Provider>
  );
}
